import "@/app/style/videoPlayerControl.scss";
import { Slider, Box, Grid, Typography } from '@mui/material';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Replay, Pause, PlayArrow, VolumeOff, VolumeUp, Fullscreen, FullscreenExit } from '@mui/icons-material';
import { MultiVideoSource, VideoMode, VideoQuality, VideoSource } from "@/app/utils/class/common/video";
import { useLanguage } from "@/app/utils/LanguageContext";
import { isIOSWithTouch } from "@/app/utils/Common";

interface IControlRef {
  isShown: boolean;
  isHovered: boolean;
  show: (callbackFunc?: Function) => void;
  hide: (callbackFunc?: Function) => void;
}

export type ControlRef = IControlRef;

interface ControlProps {
 assetPrefix: string;
 isMobile: boolean;
 curVideoSource: VideoSource[];
 curQuality?: VideoQuality;
 isPlaying: boolean;
 ended: boolean;
 currentTime: number;
 duration: number;
 played: number;
 loaded: number;
 mute: boolean;
 volume: number;
 fullscreen:boolean;
 onPlayPause: (e: any) => void;
 onSeek: (value:any) => void;
 onSeekMouseUp: (value:any) => void;
 onToggleMute: () => void;
 onVolumeChangeHandler: (e:any, value:any) => void;
 onVolumeSeekUp: (e:any, value:any) => void;
 onChangeQuality: (qlty: VideoQuality)=>void;
 onToggleFullscreen: ()=>void;
 showCurrentTooltip?: boolean;
 showHoveredTooltip?: boolean;
 dismissEffect?: 'slideDown';
 speedWeightOptions: number[];
 speedWeight: number;
 onChangeSpeed: (spdWeight: number) => void;
 enableSpeedChange?: boolean;
 
 // multi source
 multiVideoSource?: MultiVideoSource[];
 curVideoSourceType?: VideoMode;
 onChangeSource?: (type: VideoMode)=>void;
}

const generateSourceIcon = (assetPrefix:string, type: string) => {
  return type === "m" ? <img src={`${assetPrefix}/videoplayer_multiModeIcon.svg`} alt="multiple label mode icon" /> : <img src={`${assetPrefix}/videoplayer_singleModeIcon.svg`} alt="single label mode icon" />
}

const Control = forwardRef<ControlRef, ControlProps>((props, ref) => {
  const { state: lang } = useLanguage();
  const { t } = lang;
  const [showTooltip, setShowTooltip] = useState(false);
  const [cursorValue, setCursorValue] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isShowVolSlider, setIsShowVolSlider] = useState(false);
  const [sliderRect, setSliderRect] = useState<DOMRect>();
  const sliderRef = useRef<HTMLDivElement>(null);
  const currentTooltipRef = useRef<HTMLDivElement>(null);
  const hoveredTooltipRef = useRef<HTMLDivElement>(null);
  const internalRef = useRef<HTMLDivElement>(null);

  // Expose specific custom functions to the parent
  useImperativeHandle(ref, () => ({
    isShown: internalRef.current?.classList.contains('shown') ?? false,
    isHovered: isIOSWithTouch() ? false : internalRef.current?.querySelector(':hover:not(.addtionalControl):not(.baseControl):not(.sliderBox)') ? true : false,
    show: (callbackFunc?: Function) => {
      if (!internalRef.current) return;
      callbackFunc?.();
      internalRef.current.classList.replace('hidden', 'shown');
    },
    hide: (callbackFunc?: Function) => {
      if (!internalRef.current) return;
      callbackFunc?.();
      internalRef.current.classList.replace('shown', 'hidden');
    },
  }))
  
  const handleMouseEnter = (event: any) => {
    setShowTooltip(true);
    updateCursorValue(event);
    if (sliderRef.current)
      setSliderRect(sliderRef.current.getBoundingClientRect())
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const handleMouseMove = (event: any) => {
    updateCursorValue(event);
  };

  const updateCursorValue = (event: any) => {
    if (!sliderRef.current) return;
    const sliderWidth = sliderRect?.width || 0;
    const cursorPosition = event.pageX - (sliderRect?.left || 0);
    const cursorValue = (cursorPosition / sliderWidth) * 100;
    setCursorValue(cursorValue);
  };

  const formatTime = (time: any) => {
    //formarting duration of video
    if (!time || !isFinite(time) || isNaN(time)) {
      return "00:00";
    }
  
    const date = new Date(time * 1000);
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const seconds = date.getUTCSeconds().toString().padStart(2, "0");
    if (hours) {
      //if video have hours
      return `${hours}:${minutes.toString().padStart(2, "0")} `;
    } else return `${minutes}:${seconds}`;
  };

  const getTooltipPosition = (tooltipRef: React.RefObject<HTMLDivElement | null>, value: number) => {
    const tooltipWidth = tooltipRef.current ? tooltipRef.current.getBoundingClientRect().width : 0;
    const sliderWidth = sliderRect?.width || 0;
    const tooltipMin = tooltipWidth / 2;
    const tooltipMax = sliderWidth - tooltipMin;
    const tooltipPos = (value / 100) * sliderWidth;
    switch(true){
      case (tooltipPos < tooltipMin):
        return ((tooltipPos/tooltipMin) * 50) * -1
      case (tooltipPos > tooltipMax):
        return (((tooltipPos - tooltipMax)/tooltipMin * 50) + 50) * -1
      default:
        return -50
    }
  };

  const renderVideoSourceBtn = () => {
    if (props.multiVideoSource && props.multiVideoSource.length > 1){
      let count = props.multiVideoSource?.length || 1;
      return (
        <Grid item columns={8} display={"flex"} gap={"6px"}>
        {
          props.multiVideoSource?.map((s: MultiVideoSource, idx: number) => {
            if (s.type === props.curVideoSourceType) return;
            return (
              <div className={`sourceBtn clickable hoverable`} key={s.type}
                onClick={(e: any) => {e.preventDefault(); props.onChangeSource?.(s.type);}}>
                {generateSourceIcon(props.assetPrefix, s.type)}
                <Box>{t(`video_source_btn_type_${s.type}`)}</Box>
              </div>
            )
          })
        }
        </Grid>
      )
    }
  }
  return(
    <Box className={`controlContainer ${props?.dismissEffect ? props?.dismissEffect : 'normal'} shown noSelect${props?.isMobile ? '' : ' desktop'}`} id="videoControl" ref={internalRef}>
      <Grid className="addtionalControl" columns={12} container>      
      {
        !props?.isMobile && props?.enableSpeedChange &&
        <Grid item columns={4}>
          <div className="speedBtn clickable"
            onClick={(e:any) => {
              e.preventDefault();
              let targetSpd = 1;
              if (props?.speedWeightOptions && props?.speedWeightOptions.indexOf(props.speedWeight) != -1){
                const idx = props?.speedWeightOptions.indexOf(props.speedWeight) + 1;
                targetSpd = idx < props?.speedWeightOptions.length ? props?.speedWeightOptions[idx] : props?.speedWeightOptions[0];
              }
              props?.onChangeSpeed(targetSpd)
            }}>
            <Box>{props.speedWeight}x</Box>
          </div>
        </Grid>
      }
      { props?.multiVideoSource && props?.multiVideoSource?.length > 1 && renderVideoSourceBtn() }
      </Grid>
      <div className="baseControl">
      <Box className={`sliderBox${isDragging ? " dragging": ""}`}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}>
        <div className="item">
          <Slider className={`progressBar clickable${props.isMobile ? " mobile" : ""}`}
            ref={sliderRef}
            sx={{
              "& .MuiSlider-rail": {
                background: `linear-gradient(to right, white 0% ${props.loaded * 100}%, transparent ${props.loaded * 100}% 100%)`
              }
            }}
            min={0}
            max={100}
            value={props.played * 100} 
            onClick={(e: any)=>{
              e.preventDefault();
            }}
            onChange={(e: any, value: any) => {
              e.preventDefault();
              setIsDragging(true);
              props.onSeek(value)
            }}
            onChangeCommitted={(e: any, value: any) => {
              e.preventDefault();
              setIsDragging(false);
              props.onSeekMouseUp(value)
            }}/>
        </div>
        <div className="item">
          { !props.isMobile && showTooltip && (props.showCurrentTooltip || props.showHoveredTooltip) && (
            <>
            {
              props.showCurrentTooltip && (
                <div
                  className="indicator current"
                  style={{
                    left: `calc(${props.played * 100}% - 1px)`,
                  }}>
                    <div
                    ref={currentTooltipRef}
                    className="tooltip white"
                    style={{
                      transform: `translateX(${getTooltipPosition(currentTooltipRef, props.played * 100)}%)`,
                    }}> {formatTime(props.currentTime)}</div>
                </div>
              )
            }
            {
              props.showHoveredTooltip && (
                <div
                  className="indicator hovered"
                  style={{
                    left: `calc(${cursorValue}% - 1px)`,
                  }}>
                    <div
                    ref={hoveredTooltipRef}
                    className="tooltip black"
                    style={{
                      transform: `translateX(${getTooltipPosition(hoveredTooltipRef, cursorValue)}%)`,
                    }}> {formatTime(cursorValue / 100 * props.duration)}</div>
                </div>
              )
            }
            </>
          )}
        </div>
      </Box>
      <Grid className="controlBox" columns={12} container>
        <Grid item className="controlPanel left" columns={4}>
          <Grid item className="iconBtn clickable" onClick={(e) => props.onPlayPause(e)}>
            <Box style={{display: "flex"}}>
              { props.ended ? (<Replay fontSize="medium" />) : 
                  props.isPlaying ? (<Pause fontSize="medium"/>) : (<PlayArrow fontSize="medium" />)
              }
            </Box>
          </Grid>
          <Box style={{alignSelf: "center"}}>
            <Typography className="videoTimestamp">{ formatTime(props.currentTime)} / {formatTime(props.duration)}</Typography>
          </Box>
        </Grid>
        <Grid item className="controlPanel right" columns={8}>
          <Box className={`volumeSliderWrapper${props.isMobile ? '' : ' desktop'}${isShowVolSlider ? ' isShowVolSlider' : ''}`}
                onMouseEnter={(e: any) => {setIsShowVolSlider(true);}}
                onMouseLeave={(e: any) => {setIsShowVolSlider(false);}}>
            <Grid item className={"iconBtn volumeBtn clickable"} onClick={props.onToggleMute}>
              <Box style={{display: "flex"}}>
                { props.mute ? (<VolumeOff fontSize="medium" />) : (<VolumeUp fontSize="medium" />) }
              </Box>
            </Grid>
            {
              !props.isMobile && (
                <Slider
                    className={"volumeSlider clickable"}
                    value={props.volume * 100}
                    onChange={(e, value) => props.onVolumeChangeHandler(e, value)}
                    onChangeCommitted={props.onVolumeSeekUp}
                  />)
            }
          </Box>
          {
            !props.isMobile && props.curVideoSource && props.curVideoSource.length > 1 && props.curVideoSource.map((s: VideoSource, idx: number) => (
              <Grid item className={`qualityBtn animated clickable ${s.quality === props.curQuality ? 'selected': 'hoverable'}`} key={s.quality}
                onClick={(e:any) => {e.preventDefault();props.onChangeQuality(s.quality)}}>
                <Box>{t(`video_quality_btn_${s.quality}`)}</Box>
              </Grid>
            ))
          }
          <Grid item className="iconBtn clickable" onClick={() => props.onToggleFullscreen()}>
            <Box style={{display: "flex"}}>
              { props.fullscreen ? (<FullscreenExit fontSize="medium" />) : (<Fullscreen fontSize="medium" />) }
            </Box>
          </Grid>
        </Grid>
      </Grid>
      </div>
    </Box>
    )
 });

Control.displayName = 'Control'

export default Control;